import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import LinesEllipsis from 'react-lines-ellipsis';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { getFormattedDate } from 'utils/date';
import { Typography } from 'components/typography';
import { Icon } from 'components/icon/icon';
import ConditionalLink from 'components/conditional-link/conditional-link';
import Image from 'components/image';

import IcnCarotRight from 'public/assets/icons/carot-right.svg';

import { ICardCoverImage } from './card';
import styles from './card.module.scss';

export type CardNewsType =
	| 'nieuws'
	| 'nieuwsberichten'
	| 'persberichten'
	| 'subsidies'
	| 'vergunningen'
	| 'projecten'
	| 'evenementen'
	| 'in-gesprek-met';

export interface ICardNewsProps {
	id: string;
	title: string;
	bodyText: string;
	slug: string;
	excerpt: string;
	publishedAt?: string;
	from?: string;
	until?: string;
	location?: string;
	cover?: ICardCoverImage;
	hideCover?: boolean;
	type: CardNewsType;
	transparent?: boolean;
	nestedTitle?: boolean;
	priority?: boolean;
	largeImage?: boolean;
}

export const CardNews = (props: ICardNewsProps): React.JSX.Element => {
	const titleRef = useRef<HTMLAnchorElement>(null);
	const router = useRouter();
	const intl = useIntl();
	const { locale } = router;

	const [longTitle, setLongTitle] = useState<boolean>(true);

	const formattedDate = props.publishedAt && getFormattedDate(new Date(props.publishedAt), 'd MMMM y', locale);
	const formattedFromDate = props.from && getFormattedDate(new Date(props.from), 'd MMMM y', locale);
	const formattedUntilDate = props.until && getFormattedDate(new Date(props.until), 'd MMMM y', locale);
	const longText = props.excerpt.length > (longTitle ? 120 : 150);

	useEffect(() => {
		if ((titleRef.current?.clientHeight || 0) < 57) {
			setLongTitle(false);
		} else {
			setLongTitle(true);
		}
	}, [titleRef.current?.clientHeight]);

	// Construct URL, avoiding double slashes if slug already contains the type
	const constructUrl = (): string => {
		if (props.type === 'evenementen') {
			return props.slug;
		}
		// If slug already starts with /, use it as-is
		if (props.slug.startsWith('/')) {
			return props.slug;
		}
		// Otherwise, construct the full path
		return `/${props.type}/${props.slug}`;
	};

	return (
		<ConditionalLink
			href={constructUrl()}
			passHref
			condition={props.type !== 'evenementen'}
			key={props.title}
			aria-labelledby={`${props.id}-time ${props.id}-label ${props.id}-description`}
		>
			<span className={props.transparent ? styles.newsArticleItem : styles.newsArticleCard}>
				<div className={styles.main}>
					<span className={styles.articleTitle} ref={titleRef}>
						<span className={styles.icon}>
							<Icon customIcon={<IcnCarotRight />} size="12px" />
						</span>
						<Typography tag={props.nestedTitle ? 'h3' : 'h2'} variant="h5">
							<LinesEllipsis text={props.title} maxLine="2" ellipsis="..." />
						</Typography>
					</span>

					{props.type !== 'projecten' && (formattedDate || formattedFromDate) && (
						<div className={styles.publishedAt}>
							<time id={`${props.id}-time`} dateTime={props.publishedAt || props.from}>
								{formattedDate || formattedFromDate}
							</time>
							{formattedUntilDate && formattedUntilDate !== formattedDate && formattedUntilDate !== formattedFromDate && (
								<>
									{' - '}
									<time dateTime={props.until}>{formattedUntilDate}</time>
								</>
							)}
						</div>
					)}

					{props.location && (
						<div className={styles.location}>
							<strong>{intl.formatMessage({ id: 'global.location' })}:</strong> {props.location}
						</div>
					)}

					<small
						id={`${props.id}-description`}
						className={longTitle ? styles.articleBody : styles.articleBodyLong}
						title={(longText && props.excerpt) || undefined}
					>
						<LinesEllipsis
							text={props.excerpt}
							maxLine={longTitle ? '3' : '4'}
							ellipsis="..."
							trimRight
							basedOn="words"
						/>
					</small>
				</div>

				{!props.hideCover && (
					<div className={clsx(styles.image, props.type === 'projecten' ? styles.projectImage : '')}>
						<Image
							attributes={props.cover}
							style={{ objectFit: 'cover' }}
							sizes="(max-width: 768px) 720px, (max-width: 1200px) 570px, 390px"
							priority={props.priority}
							imageSize="medium"
							fill
						/>
					</div>
				)}
			</span>
		</ConditionalLink>
	);
};

export default CardNews;
